import { c as _c } from "react/compiler-runtime";
import { Editor } from '@monaco-editor/react';
import type { LanguageDefinitionResponse, TemplateAutocomplete } from '@onefootprint/request-types/dashboard';
import { type ExpressionAutocomplete, getVariantSpecificMonacoConfig, modelAutocomplete, moveFocus, Shimmer, useMonaco } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';
import { hoverProvider } from './hover-provider';
const registeredHoverLanguages = new Set<string>();
type ExpressionEditorProps = {
  value: string;
  onChange: (value: string) => void;
  /** Undefined until it has been fetched, which the dropdown shows as "Loading...". An empty
   * token list is a loaded list with nothing in it, and offers nothing. */
  autocompleteItems?: TemplateAutocomplete;
  languageDefinition?: LanguageDefinitionResponse;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  hasError?: boolean;
  variant?: 'textarea' | 'input';
  placeholder?: string;
  /**
   * Overrides the variant's own height. Pass `100%` to fill a sized container; that also drops the
   * textarea variant's fixed height and drag-to-resize handle, which would fight the container.
   */
  height?: string;
};
export const ExpressionEditor = t0 => {
  const $ = _c(70);
  const {
    value,
    onChange,
    autocompleteItems,
    languageDefinition,
    disabled: t1,
    onFocus,
    onBlur,
    className,
    hasError,
    variant: t2,
    placeholder,
    height
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "textarea" : t2;
  const {
    monaco
  } = useMonaco();
  const {
    resolvedTheme
  } = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const editorRef = useRef(null);
  const mountedModelRef = useRef(null);
  let t3;
  if ($[0] !== autocompleteItems?.tokens) {
    t3 = autocompleteItems?.tokens?.map(_temp2);
    $[0] = autocompleteItems?.tokens;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const tokens = t3;
  let t4;
  if ($[2] !== autocompleteItems?.tokens || $[3] !== languageDefinition || $[4] !== monaco) {
    t4 = () => {
      if (!monaco || !languageDefinition) {
        return;
      }
      if (registeredHoverLanguages.has("expression-editor")) {
        return;
      }
      monaco.languages.registerHoverProvider("expression-editor", hoverProvider(monaco, {
        ...languageDefinition,
        languageName: "expression-editor"
      }, autocompleteItems?.tokens ?? []));
      registeredHoverLanguages.add("expression-editor");
    };
    $[2] = autocompleteItems?.tokens;
    $[3] = languageDefinition;
    $[4] = monaco;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== languageDefinition || $[7] !== monaco) {
    t5 = [languageDefinition, monaco];
    $[6] = languageDefinition;
    $[7] = monaco;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== monaco || $[10] !== tokens) {
    t6 = () => {
      if (!monaco) {
        return;
      }
      const tokenValues = (tokens ?? []).map(_temp3).filter(Boolean);
      const rules = [];
      if (tokenValues.length > 0) {
        const escapedTokens = tokenValues.map(_temp4).join("|");
        rules.push([new RegExp(`\\b(${escapedTokens})\\b`, "g"), "keyword"]);
      }
      rules.push([/"[^"]*"/, "string"], [/'[^']*'/, "string"], [/\b\d+\.?\d*\b/, "number"], [/[+\-*/=<>!&|?]/, "operator"], [/[(),;]/, "delimiter"], [/\./, "delimiter"], [/\w+/, "identifier"]);
      monaco.languages.setMonarchTokensProvider("expression-editor", {
        tokenizer: {
          root: rules
        }
      });
    };
    t7 = [monaco, tokens];
    $[9] = monaco;
    $[10] = tokens;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  if ($[13] !== monaco || $[14] !== onBlur || $[15] !== onFocus || $[16] !== tokens || $[17] !== variant) {
    t8 = editor => {
      if (!monaco) {
        return;
      }
      editorRef.current = editor;
      const model = editor.getModel();
      if (model) {
        mountedModelRef.current = model;
        modelAutocomplete.set(model, tokens);
      }
      editor.onDidChangeModel(e => {
        if (e.oldModelUrl && mountedModelRef.current) {
          modelAutocomplete.delete(mountedModelRef.current);
        }
        const newModel = editor.getModel();
        mountedModelRef.current = newModel ?? null;
        if (newModel) {
          modelAutocomplete.set(newModel, tokens);
        }
      });
      if (variant === "input") {
        editor.onKeyDown(e_0 => {
          if (e_0.keyCode === monaco.KeyCode.Enter) {
            const isSuggestVisible = !!document.querySelector(".suggest-widget.visible");
            if (!isSuggestVisible) {
              e_0.preventDefault();
              e_0.stopPropagation();
            }
          }
          if (e_0.keyCode === monaco.KeyCode.Tab) {
            const isSuggestVisible_0 = !!document.querySelector(".suggest-widget.visible");
            if (!isSuggestVisible_0) {
              e_0.preventDefault();
              e_0.stopPropagation();
              moveFocus(!e_0.shiftKey);
            }
          }
        });
        editor.onDidPaste(() => {
          const m = editor.getModel();
          if (!m) {
            return;
          }
          const text = m.getValue();
          const singleLineText = text.replace(/\r?\n|\r/g, " ");
          if (text !== singleLineText) {
            m.setValue(singleLineText);
            const lineCount = m.getLineCount();
            const lastLineLength = m.getLineLength(lineCount);
            editor.setPosition({
              lineNumber: lineCount,
              column: lastLineLength + 1
            });
          }
        });
      }
      editor.onDidFocusEditorText(() => {
        setIsFocused(true);
        onFocus?.();
      });
      editor.onDidBlurEditorText(() => {
        setIsFocused(false);
        onBlur?.();
      });
      editor.onDidFocusEditorWidget(() => {
        setIsFocused(true);
        onFocus?.();
      });
      editor.onDidBlurEditorWidget(() => {
        setIsFocused(false);
        onBlur?.();
      });
    };
    $[13] = monaco;
    $[14] = onBlur;
    $[15] = onFocus;
    $[16] = tokens;
    $[17] = variant;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const onMount = t8;
  let t10;
  let t9;
  if ($[19] !== tokens) {
    t9 = () => {
      const model_0 = mountedModelRef.current;
      if (model_0) {
        modelAutocomplete.set(model_0, tokens);
      }
    };
    t10 = [tokens];
    $[19] = tokens;
    $[20] = t10;
    $[21] = t9;
  } else {
    t10 = $[20];
    t9 = $[21];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => () => {
      if (editorRef.current) {
        editorRef.current.dispose();
      }
    };
    t12 = [];
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  useEffect(t11, t12);
  if (!monaco) {
    const t13 = variant === "textarea" ? "h-[112px]" : "h-[328px]";
    let t14;
    if ($[24] !== t13) {
      t14 = cx("w-full", t13);
      $[24] = t13;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    let t15;
    if ($[26] !== t14) {
      t15 = <Shimmer className={t14} />;
      $[26] = t14;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    return t15;
  }
  const t13 = !!height;
  let t14;
  if ($[28] !== t13) {
    t14 = cx("overflow-visible w-full", {
      "h-full": t13
    });
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = resolvedTheme === "dark" ? "expression-editor-theme-dark" : disabled ? "expression-editor-theme-disabled" : "expression-editor-theme";
  const t16 = variant === "input";
  const t17 = variant === "input" && !disabled;
  const t18 = variant === "textarea";
  const t19 = variant === "textarea" && !height;
  const t20 = !hasError && isFocused;
  let t21;
  if ($[30] !== className || $[31] !== disabled) {
    t21 = disabled && !className?.includes("border-");
    $[30] = className;
    $[31] = disabled;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== className || $[34] !== disabled || $[35] !== hasError || $[36] !== t16 || $[37] !== t17 || $[38] !== t18 || $[39] !== t19 || $[40] !== t20 || $[41] !== t21) {
    t22 = cx("\n            relative w-full rounded-md border border-primary\n            hover:border-(--border-color-primary-hover)\n            outline-none caret-black\n            px-3\n            [&_.monaco-editor]:absolute!\n          ", {
      "overflow-hidden": t16,
      "bg-primary [&_.monaco-editor-background]:bg-primary!": t17,
      "pt-[5px] bg-secondary [&_.monaco-editor-background]:bg-secondary! overflow-hidden min-h-[80px]": t18,
      "resize-y h-[112px]": t19,
      "border-secondary! shadow-focus-accent": t20,
      "border-error!": hasError,
      "shadow-focus-error hover:shadow-none": hasError,
      "pointer-events-none bg-secondary! [&_.monaco-editor-background]:bg-secondary! [&_.monaco-editor_.margin]:bg-secondary! [&_.overflow-guard]:bg-secondary!": disabled,
      "border-tertiary!": t21
    }, className);
    $[33] = className;
    $[34] = disabled;
    $[35] = hasError;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t23 = height ?? (variant === "textarea" ? "auto" : "32px");
  let t24;
  if ($[43] !== variant) {
    t24 = variant === "textarea" ? {
      className: "min-h-[80px]"
    } : undefined;
    $[43] = variant;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== onChange) {
    t25 = value_0 => onChange(value_0 ?? "");
    $[45] = onChange;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Shimmer className="w-full h-full" />;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  const t27 = Boolean(languageDefinition);
  let t28;
  if ($[48] !== disabled || $[49] !== placeholder || $[50] !== t27 || $[51] !== variant) {
    t28 = getVariantSpecificMonacoConfig({
      variant,
      placeholder,
      size: "compact",
      disabled,
      hover: {
        enabled: t27
      },
      fontFamily: "\"DM Mono\", \"SFMono-Regular\", Consolas, \"Liberation Mono\", Menlo, Courier, monospace",
      fontWeight: "400",
      suggestLineHeight: 24,
      suggest: {
        showWords: false,
        showSnippets: true,
        showVariables: true
      },
      quickSuggestions: {
        other: true,
        comments: true,
        strings: true
      }
    });
    $[48] = disabled;
    $[49] = placeholder;
    $[50] = t27;
    $[51] = variant;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== variant) {
    t29 = variant === "input" && {
      scrollbar: {
        vertical: "hidden",
        horizontal: "hidden",
        handleMouseWheel: false
      },
      overviewRulerLanes: 0,
      scrollBeyondLastLine: false
    };
    $[53] = variant;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== t28 || $[56] !== t29) {
    t30 = {
      ...t28,
      ...t29
    };
    $[55] = t28;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== onMount || $[59] !== t15 || $[60] !== t22 || $[61] !== t23 || $[62] !== t24 || $[63] !== t25 || $[64] !== t30 || $[65] !== value) {
    t31 = <Editor theme={t15} className={t22} height={t23} wrapperProps={t24} language="expression-editor" value={value} onMount={onMount} onChange={t25} loading={t26} options={t30} />;
    $[58] = onMount;
    $[59] = t15;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
    $[64] = t30;
    $[65] = value;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== t14 || $[68] !== t31) {
    t32 = <div className={t14}>{t31}</div>;
    $[67] = t14;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  return t32;
};
function _temp(p) {
  return {
    name: p.name,
    detail: p.dataType?.join(" | ")
  };
}
function _temp2(t) {
  return {
    display: t.display,
    token: t.token,
    supportedValues: t.supportedValues,
    quoteSupportedValues: !(t.dataType?.length === 1 && t.dataType[0] === "boolean"),
    detail: t.dataType?.join(" | "),
    properties: t.properties?.map(_temp)
  };
}
function _temp3(item) {
  return item.token;
}
function _temp4(t_0) {
  return t_0.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}